import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Chip, 
  IconButton, 
  Popover,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

// Try to import build info, fallback to default if not available
let buildInfo;
try {
  buildInfo = require('../../buildInfo.js').buildInfo;
} catch (error) {
  console.warn('Build info not available:', error.message);
  buildInfo = {
    buildNumber: 'dev',
    buildTime: new Date().toISOString(),
    gitCommit: 'unknown',
    gitBranch: 'unknown',
    nodeVersion: 'unknown',
    environment: 'development'
  };
}

const BuildInfo = ({ variant = 'chip', placement = 'bottom-right' }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const formatBuildTime = (isoString) => {
    try {
      return new Date(isoString).toLocaleString();
    } catch (error) {
      return isoString;
    }
  };

  const getBuildNumberDisplay = () => {
    if (typeof buildInfo.buildNumber === 'number') {
      // Convert timestamp to more readable format
      const date = new Date(buildInfo.buildNumber);
      return `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, '0')}${date.getDate().toString().padStart(2, '0')}.${date.getHours().toString().padStart(2, '0')}${date.getMinutes().toString().padStart(2, '0')}`;
    }
    return buildInfo.buildNumber;
  };

  const getEnvironmentColor = () => {
    switch (buildInfo.environment) {
      case 'production': return 'error';
      case 'staging': return 'warning';
      case 'development': return 'info';
      default: return 'default';
    }
  };

  if (variant === 'chip') {
    return (
      <>
        <Chip
          icon={<InfoIcon />}
          label={`Build ${getBuildNumberDisplay()}`}
          size="small"
          color={getEnvironmentColor()}
          variant="outlined"
          onClick={handleClick}
          sx={{
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        />
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Paper sx={{ p: 2, minWidth: 300 }}>
            <Typography variant="h6" gutterBottom>
              Build Information
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell><strong>Build Number:</strong></TableCell>
                  <TableCell>{getBuildNumberDisplay()}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Build Time:</strong></TableCell>
                  <TableCell>{formatBuildTime(buildInfo.buildTime)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Environment:</strong></TableCell>
                  <TableCell>
                    <Chip 
                      label={buildInfo.environment} 
                      size="small" 
                      color={getEnvironmentColor()}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Git Branch:</strong></TableCell>
                  <TableCell>{buildInfo.gitBranch}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Git Commit:</strong></TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {buildInfo.gitCommit}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Node Version:</strong></TableCell>
                  <TableCell>{buildInfo.nodeVersion}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        </Popover>
      </>
    );
  }

  // Simple text variant
  return (
    <Box onClick={handleClick} sx={{ cursor: 'pointer' }}>
      <Typography variant="caption" color="text.secondary">
        v{getBuildNumberDisplay()}
      </Typography>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Paper sx={{ p: 2, minWidth: 300 }}>
          <Typography variant="h6" gutterBottom>
            Build Information
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell><strong>Build Number:</strong></TableCell>
                <TableCell>{getBuildNumberDisplay()}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Build Time:</strong></TableCell>
                <TableCell>{formatBuildTime(buildInfo.buildTime)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Environment:</strong></TableCell>
                <TableCell>
                  <Chip 
                    label={buildInfo.environment} 
                    size="small" 
                    color={getEnvironmentColor()}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Git Branch:</strong></TableCell>
                <TableCell>{buildInfo.gitBranch}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Git Commit:</strong></TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {buildInfo.gitCommit}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Node Version:</strong></TableCell>
                <TableCell>{buildInfo.nodeVersion}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      </Popover>
    </Box>
  );
};

export default BuildInfo;
